/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { uniqBy } from '@toss/utils';
import { z } from 'zod';

import { adapterRouteRegex } from '@fai/constants/regex';
import { DedicatedModelType } from '@fai/friendli-suite/features/types/dedicated-endpoints/models';

const adaptersSchema = z
  .array(
    z.object({
      id: z.string().min(1),
      name: z.string().nullish(),
      route: z.string().min(1).regex(adapterRouteRegex, { message: 'Adapter route is invalid.' }),
      editDisabled: z.boolean().nullish(),
    })
  )
  .refine((schema) => schema.length === uniqBy(schema, (item) => item.route).length, {
    message: 'Adapter route should be unique.',
  });

export const endpointModelSchema = z.object({
  type: z
    .union([
      z.literal(DedicatedModelType.HuggingFace),
      z.literal(DedicatedModelType.Wandb),
      z.literal(DedicatedModelType.Native),
    ])
    .nullable(),

  huggingFace: z.object({
    repositoryName: z.string(),
    repositoryRevision: z.string().nullable(),
    adapters: adaptersSchema,
  }),

  wandb: z.object({
    artifactName: z.string(),
    adapters: adaptersSchema,
  }),

  native: z.object({
    modelId: z.string(),
    modelName: z.string(),
    adapters: adaptersSchema,
  }),
});

export const finetuningModelSchema = z.object({
  type: z
    .union([
      z.literal(DedicatedModelType.HuggingFace),
      z.literal(DedicatedModelType.Wandb),
      z.literal(DedicatedModelType.Native),
    ])
    .nullable(),

  huggingFace: z.object({
    repositoryName: z.string(),
    repositoryRevision: z.string().nullable(),
  }),

  wandb: z.object({
    artifactName: z.string(),
  }),

  native: z.object({
    modelId: z.string(),
    modelName: z.string(),
  }),
});
